import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@/context/NotificationContext";
import { BaseEntity } from "@/components/EntityPickerModal/EntityPickerModal";
import { fetchAdminUsers } from "@/modules/administration/features/UserManagement/api/fetchAdminUsers/fetchAdminUsersApi";
import { AdminUser } from "@/modules/administration/features/UserManagement/api/fetchAdminUsers/types";
import { fetchRecurringEmails } from "@/modules/emailManagement/features/RecurringEmailManagement/api/fetchRecurringEmails/fetchRecurringEmailsApi";
import { RecurringEmail } from "@/modules/emailManagement/features/RecurringEmailManagement/api/fetchRecurringEmails/types";
import { subscribeRecurringEmailRecipients } from "@/modules/emailManagement/features/RecurringEmailManagement/api/subscribeRecurringEmailRecipients/subscribeRecurringEmailRecipientsApi";

const RECURRING_EMAILS_PATH = "/admin/email/recurring";

interface EntityOptionsRequest {
  searchTerm: string;
  page: number;
  pageSize: number;
}

function formatEmployeeLabel(user: AdminUser): string {
  return `${user.firstName} ${user.lastName} (${user.email})`;
}

function describeSubscribeResult(
  subscribedCount: number,
  alreadySubscribedCount: number,
): string {
  const subscribed = `${subscribedCount} subscribed`;

  if (alreadySubscribedCount === 0) {
    return subscribed;
  }

  return `${subscribed}, ${alreadySubscribedCount} already subscribed`;
}

export function useAddRecurringEmailSubscriberPage() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [recurringEmails, setRecurringEmails] = useState<RecurringEmail[]>([]);
  const [recurringEmail, setRecurringEmail] = useState("");
  const [employees, setEmployees] = useState<BaseEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadRecurringEmails = async () => {
      try {
        const response = await fetchRecurringEmails();
        setRecurringEmails(response.data);
        setRecurringEmail(response.data[0]?.recurringEmail ?? "");
      } catch {
        // Error handled by axios interceptor
      } finally {
        setIsLoading(false);
      }
    };

    loadRecurringEmails();
  }, []);

  const fetchEmployeeOptions = useCallback(
    async ({ searchTerm, page, pageSize }: EntityOptionsRequest) => {
      const response = await fetchAdminUsers({ searchTerm, page, pageSize });
      const users = response.data.users;

      return {
        data: users.map((user) => ({
          id: user.employeeUuid,
          name: formatEmployeeLabel(user),
        })),
        totalCount: response.meta?.totalCount ?? users.length,
      };
    },
    [],
  );

  const canSubmit =
    recurringEmail !== "" && employees.length > 0 && !isSubmitting;

  const submit = useCallback(async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);

    try {
      const response = await subscribeRecurringEmailRecipients(recurringEmail, {
        employeeUuids: employees.map((employee) => String(employee.id)),
      });

      showNotification(
        describeSubscribeResult(
          response.data.subscribedCount,
          response.data.alreadySubscribedCount,
        ),
        "",
        "success",
      );
      navigate(RECURRING_EMAILS_PATH);
    } catch {
      // Error handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, recurringEmail, employees, showNotification, navigate]);

  const cancel = useCallback(() => {
    navigate(RECURRING_EMAILS_PATH);
  }, [navigate]);

  return {
    recurringEmails,
    recurringEmail,
    setRecurringEmail,
    employees,
    setEmployees,
    fetchEmployeeOptions,
    isLoading,
    isSubmitting,
    canSubmit,
    submit,
    cancel,
  };
}
